<template>
  <div>
    <!-- メンタリングスケジュール -->
    <table class="table whitespace-nowrap border-b border-b-[#dee2e6] text-center">
      <thead>
        <tr>
          <th class="ya-te-fixed-head ya-te-time-column w-12 md:w-[3.75rem]" />
          <th
            v-for="day in appointmentDays"
            :key="day"
            class="ya-te-fixed-head p-2 text-center font-bold text-white"
          >
            <span
              class="block text-[0.75rem] leading-tight md:text-[1rem]"
              v-html="dateToStr(day)"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(time, rowIndex) in appintmentTimes"
          :key="rowIndex"
          class="border-b-[#dee2e6]"
        >
          <td class="px-3 align-middle text-[0.75rem] md:text-[1rem]">
            {{ time }}
          </td>
          <template
            v-for="(tab, columnIndex) in appointmentTable[rowIndex]"
            :key="columnIndex"
          >
            <td
              v-if="tab"
              class="p-1 align-middle"
            >
              <!-- モーダルを開くボタン -->
              <button
                class="flex w-full items-center justify-center rounded bg-white p-2.5 font-bold shadow-[0_0_4px_rgba(51,51,51,0.2)] hover:bg-[#fdebee] md:p-3"
                @click="sendAppointmentDate(time, columnIndex)"
              >
                <FontAwesomeIcon
                  :icon="faCircle"
                  class="fa-fw ya-te-text-green size-4 md:size-6"
                />
              </button>
            </td>
            <td
              v-else
              class="co-display-13 p-1 align-middle leading-tight text-co-gray-400"
            >
              空席
              <br class="inline md:hidden">
              なし
            </td>
          </template>
        </tr>
      </tbody>
    </table>

    <!-- モーダル -->
    <YaTeCounselingCalendarModal
      ref="calendarModal"
      :appointment-form="appointmentForm"
      :appointment-date="appointmentDate"
      :courses="courses"
      :purpose="purpose"
      :course-screen-name="courseScreenName"
      :page-url="pageUrl"
    />
  </div>
</template>

<script lang="ts">
import { daysOfWeek } from '~/utility/utility'
import YaTeCounselingCalendarModal from '~/components/YaTe/YaTeCounseling/YaTeCounselingCalendarModal.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircle } from '@awesome.me/kit-65d95819ce/icons/classic/regular'

interface Data {
  appointmentDate: Date | null
}

interface Methods {
  create1WeeksArray: (date: Date | null) => Date[] | null[]
  transpose: (arr: boolean[][]) => boolean[][]
  sendAppointmentDate: (time: string, index: number) => void
  dateToStr: (date: Date) => string
}

interface Computed {
  table: boolean[][]
  appointmentDays: Date[] | null[]
  appintmentTimes: string[]
  appointmentTable: boolean[][]
}

interface Props {
  appointmentForm: any
  appointmentFormTable: any
  courses: string | null
  purpose: string | null
  courseScreenName: string
  pageUrl: string
}

const options = {
  async setup() {
    // モーダルの開閉制御

    const calendarModal = ref(null)

    const showModal = () => {
      if (calendarModal.value) {
        calendarModal.value.showModal()
      }
    }

    return {
      faCircle,
      calendarModal,
      showModal,
    }
  },
  props: {
    appointmentForm: {
      default: {},
    },
    appointmentFormTable: {
      default: null,
    },
    courses: {
      default: null,
    },
    purpose: {
      default: null,
    },
    courseScreenName: {
      default: null,
    },
    pageUrl: {
      default: '',
    },
  },
  data() {
    return {
      appointmentDate: null,
    }
  },
  mounted() {},
  computed: {
    table() {
      if (!this.appointmentFormTable) return null
      return this.appointmentFormTable.table
    },
    appointmentDays() {
      if (!this.appointmentForm) return this.create1WeeksArray(null)
      const submittedBeginsAtDate = new Date(
        this.appointmentForm.submitted_begins_at
      )
      return this.create1WeeksArray(submittedBeginsAtDate)
    },
    appintmentTimes() {
      const rangeFrom0To31 = Array.from(Array(32), (_, k) => k)
      const times = rangeFrom0To31.map((num, i) => {
        if (i % 2 === 0) {
          return `${8 + num / 2}:00`
        } else {
          return `${Math.floor(8 + (num - 1) / 2)}:30`
        }
      })
      return times
    },
    appointmentTable() {
      if (!this.table) {
        const initialValue = [...Array(32)].map(() => {
          return [...Array(7)].map(() => {
            return false
          })
        })
        return initialValue
      } else {
        return this.transpose(this.table)
      }
    },
  },
  methods: {
    create1WeeksArray(date: Date | null): Date[] | null[] {
      if (!date) return [...Array(7)].map(() => null)
      const today = new Date(date) as Date
      const days = [...Array(7)].map((_, index) => {
        const day = new Date(date)
        day.setDate(today.getDate() + index)
        day.setHours(0)
        day.setMinutes(0)
        day.setSeconds(0)
        return day
      })
      return days
    },
    transpose(a) {
      return a[0].map((_, c) => a.map((r) => r[c]))
    },
    sendAppointmentDate(time, index) {
      const day = this.appointmentDays[index]
      if (!day) return null
      const [hour, minute] = time.split(':')
      const date = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        Number(hour),
        Number(minute)
      )
      this.appointmentDate = date
      // モーダルを開く
      this.showModal()
    },
    dateToStr(date) {
      if (date == null) return '-/- (-)'
      return `${date.getMonth() + 1}/${date.getDate()}<br>(${daysOfWeek(date)})`
    },
  },
  components: {
    YaTeCounselingCalendarModal,
    FontAwesomeIcon,
  },
}

export default defineNuxtComponent(options)
</script>

<style scoped>
@import url("~/assets/css/YaTe/YaTeCommon/YaTeCommon.css");

.table {
  table-layout: fixed;
}

.ya-te-fixed-head {
  position: sticky;
  top: 0;
  background: #00003e;
}
</style>
